import { useEffect, useRef, useState } from 'react'
import './PullToRefresh.css'
import { DRAW_MS, Spinner } from './Spinner'

/** How far the finger travels before the release actually reloads. */
const THRESHOLD = 70
/** Where the indicator stops, so a long drag doesn't stretch it forever. */
const MAX = 110
/**
 * Ignore drags that start with a horizontal intent, the cart sheet and the
 * category rows scroll sideways, and a diagonal swipe there should not arm a
 * reload.
 */
const SLOP = 12

/**
 * Pull down from the top to reload.
 *
 * Chrome on Android has this built in, but an installed PWA on iOS does not:
 * standalone mode has no browser chrome and no gesture of its own, so a waiter
 * on an older build had no way to pick up a new one short of force-quitting the
 * app. This gives every platform the same gesture, which matters here because
 * a restaurant gets fixes deployed mid-service.
 *
 * Implemented on touch events rather than CSS overscroll so it behaves the same
 * in an iOS PWA, in Chrome, and inside the Android APK's webview.
 */
export function PullToRefresh() {
  const [pull, setPull] = useState(0)
  const [busy, setBusy] = useState(false)
  // Refs, not state: these are read inside the move handler on every frame and
  // re-rendering to store them would fight the gesture.
  const startY = useRef(0)
  const startX = useRef(0)
  const active = useRef(false)
  const armed = useRef(false)

  useEffect(() => {
    /**
     * The nearest scrollable ancestor of whatever was touched.
     *
     * The pages scroll in their own containers, not on the document, so asking
     * the window whether it is at the top would arm the gesture halfway down a
     * ticket list. This walks up from the touch instead.
     */
    const scrollerFor = (node: EventTarget | null): Element | null => {
      let el = node instanceof Element ? node : null
      while (el) {
        const style = getComputedStyle(el)
        const scrolls = /auto|scroll|overlay/.test(style.overflowY)
        if (scrolls && el.scrollHeight > el.clientHeight) return el
        el = el.parentElement
      }
      return null
    }

    const onStart = (e: TouchEvent) => {
      if (busy || e.touches.length !== 1) return
      const t = e.touches[0]

      // A dialog is open: pulling the page behind it would reload mid-edit.
      if (document.querySelector('.dlg')) return

      // A drag handle (menu sorting): pulling it down moves a row, not the page.
      if (t.target instanceof Element && t.target.closest('[data-no-pull]')) return

      const scroller = scrollerFor(t.target)
      // No scroller at all still counts as "at the top", a short page has
      // nothing to scroll and should still be refreshable.
      if (scroller && scroller.scrollTop > 0) return

      startY.current = t.clientY
      startX.current = t.clientX
      active.current = true
      armed.current = false
    }

    const onMove = (e: TouchEvent) => {
      if (!active.current || busy) return
      if (document.documentElement.classList.contains('is-sorting')) {
        active.current = false
        setPull(0)
        return
      }
      const t = e.touches[0]
      const dy = t.clientY - startY.current
      const dx = t.clientX - startX.current

      if (!armed.current) {
        // Decide once, on the first meaningful movement, whether this drag is
        // ours. A sideways swipe hands the gesture back to the page.
        if (Math.abs(dx) > SLOP && Math.abs(dx) > Math.abs(dy)) {
          active.current = false
          return
        }
        if (dy < SLOP) return
        armed.current = true
      }

      if (dy <= 0) {
        setPull(0)
        return
      }

      // Resistance: the indicator moves less than the finger, so the pull feels
      // like it is against something and a stray drag doesn't reach the
      // threshold by accident.
      const distance = Math.min(MAX, dy * 0.5)
      setPull(distance)
      // Only once we own the gesture, so the browser keeps normal scrolling
      // when the drag turned out not to be a pull.
      if (e.cancelable) e.preventDefault()
    }

    const onEnd = () => {
      if (!active.current) return
      active.current = false
      armed.current = false

      setPull((current) => {
        if (current < THRESHOLD) return 0
        setBusy(true)
        // Held at the threshold while the reload runs, so the spinner does not
        // snap back before the page goes.
        //
        // Through the app's own refresh, not location.reload(): the service
        // worker serves from cache, so a plain reload would return the build
        // the user is pulling to get away from.
        //
        // Logonun çizimi bitmeden sayfa gitmiyor: bekleme __adissooRefresh'in
        // içinde, çünkü sayfayı yenileyen de o. Dışarıda beklemek yetmiyordu,
        // hızlı bağlantıda kendi reload'unu animasyonun ortasında çağırıyordu.
        //
        if (window.__adissooRefresh) void window.__adissooRefresh(DRAW_MS)
        else window.setTimeout(() => window.location.reload(), DRAW_MS)
        return THRESHOLD
      })
    }

    // Passive false on move only: that is the one that calls preventDefault.
    document.addEventListener('touchstart', onStart, { passive: true })
    document.addEventListener('touchmove', onMove, { passive: false })
    document.addEventListener('touchend', onEnd, { passive: true })
    document.addEventListener('touchcancel', onEnd, { passive: true })
    return () => {
      document.removeEventListener('touchstart', onStart)
      document.removeEventListener('touchmove', onMove)
      document.removeEventListener('touchend', onEnd)
      document.removeEventListener('touchcancel', onEnd)
    }
  }, [busy])

  if (pull === 0 && !busy) return null

  const ready = pull >= THRESHOLD

  return (
    <div className="ptr" style={{ transform: `translateY(${pull}px)` }} aria-hidden="true">
      {/* Çekerken dönen ok, bırakınca logo. İkisi iki ayrı soruyu
          cevaplıyor: ok "ne kadar daha çekmeliyim" diyor ve parmakla birlikte
          dönüyor, logo ise "artık bende, bekle" diyor. Ok bırakıldıktan sonra
          da dönseydi uygulamanın kendi işareti hiç görünmezdi. */}
      {busy ? (
        <Spinner />
      ) : (
        <div className={`ptr__disc ${ready ? 'is-ready' : ''}`}>
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
               strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
               style={{ transform: `rotate(${pull * 3}deg)` }}>
            <path d="M21 12a9 9 0 1 1-3-6.7" />
            <path d="M21 3v6h-6" />
          </svg>
        </div>
      )}
      <span className="ptr__label">
        {busy ? 'Yenileniyor…' : ready ? 'Bırakın' : 'Yenilemek için çekin'}
      </span>
    </div>
  )
}
